import React, { useState } from 'react'
import {
  Link,
  Button,
  Flex,
  Input,
  Box,
  useModalContext,
  ConfirmationDialog,
} from '@sketch/components'
import {
  Section,
  Title,
  Description,
} from '../WorkspaceSettingsProfile/WorkspaceSettingsProfile.styles'
import {
  DocumentPreviewsLayout,
  DocumentPreview,
  PreviewRow,
} from './DocumentPreviewsLayout'

const mockDocuments: DocumentPreview[] = [
  {
    uuid: '1',
    name: 'Document 1',
    previews: [
      {
        uuid: '1',
        name: 'Preview 1.1',
        passwordProtected: true,
        expiryDate: null,
      },
      {
        uuid: '2',
        name: 'Preview 1.2',
        passwordProtected: false,
        expiryDate: '2025-09-01',
      },
    ],
  },
  {
    uuid: '2',
    name: 'Document 2',
    previews: [
      {
        uuid: '1',
        name: 'Preview 2.1',
        passwordProtected: true,
        expiryDate: '2025-08-01',
      },
    ],
  },
  {
    uuid: '3',
    name: 'Document 3',
    previews: [
      {
        uuid: '1',
        name: 'Preview 3.1',
        passwordProtected: false,
        expiryDate: null,
      },
      {
        uuid: '2',
        name: 'Preview 3.2',
        passwordProtected: false,
        expiryDate: null,
      },
      {
        uuid: '3',
        name: 'Preview 3.3',
        passwordProtected: false,
        expiryDate: null,
      },
    ],
  },
]

export interface WorkspaceSettingsDocumentPreviewsProps {
  workspaceIdentifier: string
}

export const WorkspaceSettingsDocumentPreviews: React.FC<
  WorkspaceSettingsDocumentPreviewsProps
> = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const { showModal, hideModal } = useModalContext()

  const handleRefreshAll = () =>
    showModal(ConfirmationDialog, {
      title: 'Refresh All Preview Links?',
      children: (
        <p>
          Existing preview links will no longer work and you&apos;ll need to
          share new links with anyone who needs them.
        </p>
      ),
      confirmButton: {
        text: 'Refresh',
        variant: 'primary',
      },
      cancelButton: {
        text: 'Cancel',
      },
      onConfirm: () => {
        hideModal()
        // @TODO: hook this up to backend
      },
      onHide: () => {
        hideModal()
      },
    })

  const handleRemoveAll = () =>
    showModal(ConfirmationDialog, {
      title: 'Remove All Document Previews?',
      children: (
        <p>
          Any existing preview links will no longer work and no-one will be able
          to access them.
        </p>
      ),
      confirmButton: {
        text: 'Remove All',
      },
      cancelButton: {
        text: 'Cancel',
      },
      onConfirm: () => {
        hideModal()
        // @TODO: hook this up to backend
      },
      onHide: () => {
        hideModal()
      },
    })

  const handleCopyLink = (documentUUID: string, previewUUID: string) => {
    alert(`Copy link for ${documentUUID} - ${previewUUID}`)
  }

  const handlePreviewSettings = (documentUUID: string, previewUUID: string) => {
    alert(`Settings for ${documentUUID} - ${previewUUID}`)
  }

  const handleOpenPreview = (documentUUID: string, previewUUID: string) => {
    alert(`Open for ${documentUUID} - ${previewUUID}`)
  }

  const items = mockDocuments.filter(
    document =>
      document.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      document.previews.some(preview =>
        preview.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
  )

  return (
    <>
      <Section>
        <Title bold>Document Previews</Title>
        <Description>
          With Previews, you can control which pages and prototypes of a Sketch
          Document to share, and who you share them with.{' '}
          {/* @TODO: add link for learn more */}
          <Link external href="https://sketch.com">
            Learn More
          </Link>
        </Description>
        <Flex mt={4}>
          <Button onClick={handleRefreshAll} variant="secondary">
            Refresh all links
          </Button>
          <Button onClick={handleRemoveAll} variant="negative-secondary">
            Remove all previews
          </Button>
        </Flex>
      </Section>

      <Section secondary>
        <Title>{mockDocuments.length} Document Previews</Title>

        <Box mt={4} mb={6}>
          <Input
            placeholder="Search..."
            type="text"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
          />
        </Box>

        <DocumentPreviewsLayout
          header={[
            {
              label: 'Document',
              sortKey: 'name',
              sort: true,
              iconVisible: true,
            },
            { label: 'Preview' },
          ]}
          items={items}
          renderItem={(document: DocumentPreview) => (
            <PreviewRow
              document={document}
              onCopyLink={handleCopyLink}
              onPreviewSettings={handlePreviewSettings}
              onOpenPreview={handleOpenPreview}
            />
          )}
        />
      </Section>
    </>
  )
}
